import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { useAuth } from '../auth/AuthProvider';
import { db, getMeta, setMeta } from '../offline/db';
import { onSyncState, syncNow, type SyncState } from '../offline/sync';
import { fetchHouseholdProgress, type HouseholdProgress } from '../data/reads';
import { setTurfCompleted } from '../data/organizer';
import { daysUntilElection, homesPerDay } from '../lib/election';
import { isBasemapCached } from '../offline/basemap';
import { PendingBadge } from '../components/PendingBadge';
import { TurfDownload } from '../components/TurfDownload';
import { InstallBanner } from '../components/InstallBanner';

/** Last progress figures from the server, so the card still reads offline. */
const PROGRESS_KEY = 'household_progress';

function ProgressCard({
  lastSyncAt,
  campaignWide,
}: {
  lastSyncAt: string | null | undefined;
  campaignWide: boolean;
}) {
  const [progress, setProgress] = useState<HouseholdProgress | null>(null);

  useEffect(() => {
    void getMeta(PROGRESS_KEY).then((cached) => {
      if (cached) setProgress((p) => p ?? (JSON.parse(cached) as HouseholdProgress));
    });
  }, []);

  // Re-asked after every sync, which is when this device's knocks reach the
  // server and so the moment the numbers can have moved. Failing quietly is
  // right: offline, the cached figures above are the best answer there is.
  useEffect(() => {
    let live = true;
    fetchHouseholdProgress()
      .then((next) => {
        if (!live) return;
        setProgress(next);
        void setMeta(PROGRESS_KEY, JSON.stringify(next));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [lastSyncAt]);

  if (!progress) return null;

  const daysLeft = daysUntilElection();
  const perDay = homesPerDay(progress.remaining, daysLeft);
  // Unknocked homes in turf marked complete: not knocked, but not left to do
  // either. Said out loud so the three numbers visibly add up.
  const writtenOff = progress.total - progress.knocked - progress.remaining;

  return (
    <section className="progress" aria-label="Progress to election day">
      <h2 className="progress__title">
        {campaignWide ? 'Campaign' : 'Your turf'} · election Nov 3
      </h2>
      <dl className="progress__stats">
        <div>
          <dt>Total homes</dt>
          <dd>{progress.total.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Knocked</dt>
          <dd>{progress.knocked.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Remaining</dt>
          <dd>{progress.remaining.toLocaleString()}</dd>
        </div>
        <div className="progress__pace">
          <dt>Per day</dt>
          <dd>{perDay === null ? '—' : perDay.toLocaleString()}</dd>
        </div>
      </dl>
      <p className="progress__note">
        {perDay === null
          ? 'Election day.'
          : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left to knock every remaining home.`}
        {writtenOff > 0
          ? ` ${writtenOff.toLocaleString()} unknocked in completed neighborhoods not counted.`
          : ''}
      </p>
    </section>
  );
}

export function TurfListScreen() {
  const { profile, signOut } = useAuth();
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const canOrganize = profile?.role === 'organizer' || profile?.role === 'admin';

  // A device that has never synced has an empty store, which looks exactly
  // like having no assignment. Telling a volunteer on their first login that
  // they have no turf -- seconds before it arrives -- is the wrong end of that
  // ambiguity, so the two states are distinguished by the sync clock.
  const [sync, setSync] = useState<SyncState | null>(null);
  useEffect(() => onSyncState(setSync), []);
  const neverSynced = sync === null || sync.lastSyncAt === null;

  // Held here rather than per card: one archive covers every turf, so asking
  // the cache once and sharing the answer keeps the rows consistent with each
  // other the moment a download finishes.
  const [basemapReady, setBasemapReady] = useState<boolean | null>(null);
  useEffect(() => {
    void isBasemapCached().then(setBasemapReady);
  }, []);

  // From the device store, so the turf list works with no signal at all.
  const turfs = useLiveQuery(async () => {
    // Completed turf is evicted by the next sync; filtering here as well hides
    // it the moment it is marked, rather than a sync later.
    const all = (await db.turfs.toArray()).filter((t) => !t.completed_at);
    return Promise.all(
      all.map(async (t) => {
        const households = (
          await db.households.where('turf_id').equals(t.id).toArray()
        ).filter((h) => h.active);
        const visits = await db.visits
          .where('household_id')
          .anyOf(households.map((h) => h.id))
          .toArray();
        const knocked = new Set(visits.map((v) => v.household_id));
        return { ...t, household_count: households.length, visited_count: knocked.size };
      }),
    );
  }, []);

  return (
    <main className="page">
      <header className="page__head">
        <div>
          <h1>My turf</h1>
          <p className="page__sub">{profile?.name}</p>
        </div>
        <Link to="/sync" className="pending-link">
          <PendingBadge />
        </Link>
      </header>

      <ProgressCard lastSyncAt={sync?.lastSyncAt} campaignWide={canOrganize} />

      {completeError ? <p className="banner banner--error">{completeError}</p> : null}

      {turfs === undefined || (turfs.length === 0 && neverSynced) ? (
        <p className="muted">Loading your turf…</p>
      ) : null}

      {turfs?.length === 0 && !neverSynced ? (
        <p className="empty">
          No turf yet. An organizer assigns turf from the dashboard; it appears
          here after the next sync.
        </p>
      ) : null}

      <ul className="turfs">
        {turfs?.map((t) => {
          const pct =
            t.household_count === 0
              ? 0
              : Math.round((t.visited_count / t.household_count) * 100);
          return (
            <li key={t.id}>
              <Link className="turf" to={`/turf/${t.id}`}>
                <div className="turf__row">
                  <span className="turf__name">{t.name}</span>
                  <span className="turf__pct">{pct}%</span>
                </div>
                <div className="bar">
                  <div className="bar__fill" style={{ width: `${pct}%` }} />
                </div>
                <p className="turf__meta">
                  {t.visited_count} of {t.household_count} doors knocked
                </p>
              </Link>
              <TurfDownload
                householdCount={t.household_count}
                basemapReady={basemapReady}
                onBasemapCached={setBasemapReady}
              />
              {canOrganize ? (
                <div className="turf__actions">
                  <button
                    className="btn btn--link btn--small"
                    onClick={() => {
                      if (
                        !window.confirm(
                          `Mark "${t.name}" complete? It leaves everyone's turf list. ` +
                            `Reopen it from Organize if you need it back.`,
                        )
                      ) {
                        return;
                      }
                      setCompleteError(null);
                      setTurfCompleted(t.id, true)
                        .then(async () => {
                          await db.turfs.update(t.id, {
                            completed_at: new Date().toISOString(),
                          });
                          void syncNow();
                        })
                        .catch((cause: unknown) => {
                          setCompleteError(
                            cause instanceof Error
                              ? `Could not mark complete: ${cause.message}`
                              : 'Could not mark complete. Check your connection.',
                          );
                        });
                    }}
                  >
                    Mark neighborhood complete
                  </button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {canOrganize ? (
        <Link className="btn btn--secondary organize-link" to="/organize">
          Organize turf
        </Link>
      ) : null}

      <InstallBanner />

      {signOutError ? <p className="banner banner--error">{signOutError}</p> : null}

      <button
        className="btn btn--link page__signout"
        onClick={() => {
          setSignOutError(null);
          // Sign-out clears the device, so it refuses while visits are still
          // queued. Say so here rather than letting the tap do nothing.
          signOut().catch((cause: unknown) => {
            setSignOutError(
              cause instanceof Error ? cause.message : 'Could not sign out.',
            );
          });
        }}
      >
        Sign out
      </button>
    </main>
  );
}
